import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { apiErrorMsg, formatBRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Search, Sparkles, MapPin, Users, CheckCircle2, XCircle, Filter, ExternalLink, DollarSign } from "lucide-react";

const ESTAGIO_META = {
  // N1 (Prospecção/Fechamento)
  indicacao: { label: "Indicação", color: "bg-sky-100 text-sky-800 border-sky-300" },
  prospeccao: { label: "Prospecção", color: "bg-sky-100 text-sky-800 border-sky-300" },
  qualificado: { label: "Qualificado", color: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  reuniao: { label: "Reunião", color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  proposta: { label: "Proposta", color: "bg-violet-100 text-violet-800 border-violet-300" },
  documentacao: { label: "Documentação", color: "bg-amber-100 text-amber-800 border-amber-300" },
  contrato_assinado: { label: "Contrato Assinado", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  perdido: { label: "Perdido", color: "bg-red-100 text-red-800 border-red-300" },
  // N2 (Requalificação)
  entrada: { label: "Requalificação · Entrada", color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
  nutricao: { label: "Requalificação · Nutrição", color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
  monitoramento_gatilho: { label: "Requalificação · Mon. Gatilho", color: "bg-purple-100 text-purple-800 border-purple-300" },
  gatilho_ativado: { label: "Requalificação · Gatilho Ativado", color: "bg-pink-100 text-pink-800 border-pink-300" },
  requalificado: { label: "Requalificado", color: "bg-rose-100 text-rose-800 border-rose-300" },
  // Legacy (fallback para cards antigos ainda não migrados)
  indicacao_prospeccao: { label: "Prospecção", color: "bg-sky-100 text-sky-800 border-sky-300" },
  qualificacao: { label: "Qualificação", color: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  requalificacao: { label: "Requalificação", color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
};

export default function CelCarteira() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [ufsAtuacao, setUfsAtuacao] = useState([]);
  const [vendedoraScope, setVendedoraScope] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [ufFilter, setUfFilter] = useState("");
  const [somenteLivres, setSomenteLivres] = useState(false);
  const [prospectMun, setProspectMun] = useState(null);
  const [prospForm, setProspForm] = useState({ contato_nome: "", contato_cargo: "", contato_whatsapp: "", canal: "ligacao", descricao: "", data_retorno: "", observacoes: "" });
  const [pessoasMun, setPessoasMun] = useState([]);
  const [pessoasLoading, setPessoasLoading] = useState(false);
  const [selectedPessoa, setSelectedPessoa] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (ufFilter) params.set("uf", ufFilter);
    if (somenteLivres) params.set("somente_livres", "true");
    api.get(`/cel/carteira?${params.toString()}`)
      .then((r) => {
        setItems(r.data.municipios || []);
        setUfsAtuacao(r.data.ufs_atuacao || []);
        setVendedoraScope(r.data.vendedora_scope || false);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ufFilter, somenteLivres]);

  const filtered = useMemo(() => {
    if (!busca.trim()) return items;
    const q = busca.trim().toLowerCase();
    return items.filter((m) => m.nome.toLowerCase().includes(q));
  }, [items, busca]);

  const kpi = useMemo(() => ({
    total: items.length,
    livres: items.filter((m) => !m.no_funil_cel).length,
    ativos: items.filter((m) => m.no_funil_cel).length,
  }), [items]);

  const abrirProspectar = async (m) => {
    setProspectMun(m);
    setProspForm({ contato_nome: "", contato_cargo: "", contato_whatsapp: "", canal: "ligacao", descricao: "", data_retorno: "", observacoes: "" });
    setSelectedPessoa(null);
    setPessoasLoading(true);
    setPessoasMun([]);
    try {
      const r = await api.get(`/pessoas?municipio_id=${m.id}`);
      setPessoasMun(r.data || []);
    } catch (err) {
      setPessoasMun([]);
    } finally {
      setPessoasLoading(false);
    }
  };

  const escolherPessoa = (p) => {
    setSelectedPessoa(p);
    setProspForm((f) => ({
      ...f,
      contato_nome: p.nome || "",
      contato_cargo: p.funcao || "",
      contato_whatsapp: p.whatsapp || p.telefone || "",
    }));
  };

  const limparPessoa = () => {
    setSelectedPessoa(null);
  };

  const enviarProspeccao = async () => {
    if (!prospectMun) return;
    if (!prospForm.contato_nome || !prospForm.contato_whatsapp) {
      toast.error("Informe pelo menos contato e WhatsApp.");
      return;
    }
    if (!prospForm.descricao.trim()) {
      toast.error("Registre o que foi conversado no primeiro contato.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        municipio_id: prospectMun.id,
        contato_nome: prospForm.contato_nome,
        contato_cargo: prospForm.contato_cargo || null,
        contato_whatsapp: prospForm.contato_whatsapp,
        canal: prospForm.canal || "ligacao",
        descricao: prospForm.descricao.trim(),
        data_retorno: prospForm.data_retorno || null,
        observacoes: prospForm.observacoes || null,
      };
      await api.post("/cel/carteira/prospectar", payload);
      toast.success(`${prospectMun.nome}/${prospectMun.uf} entrou em Qualificação · prospecção fria atribuída a você`);
      setProspectMun(null);
      load();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Erro ao prospectar."));
    } finally { setBusy(false); }
  };

  const ufsDisp = useMemo(() => Array.from(new Set(items.map((m) => m.uf))).sort(), [items]);

  return (
    <div className="space-y-4" data-testid="cel-carteira-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Wallet className="w-7 h-7 text-sky-600" /> Carteira CeL
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {vendedoraScope ? (
              <>Municípios das suas UFs de atuação: <b>{ufsAtuacao.join(" · ") || "nenhuma definida"}</b>. Clique para prospectar.</>
            ) : (
              <>Visão executiva — todos os municípios do CRM (filtre por UF).</>
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Total exibido</div>
          <div className="text-3xl font-bold text-slate-900 mt-1">{kpi.total}</div>
        </div>
        <div className="bg-white border border-emerald-200 rounded-2xl p-4">
          <div className="text-[10px] uppercase text-emerald-700 font-bold tracking-wider">Livres (para prospectar)</div>
          <div className="text-3xl font-bold text-emerald-800 mt-1">{kpi.livres}</div>
        </div>
        <div className="bg-white border border-amber-200 rounded-2xl p-4">
          <div className="text-[10px] uppercase text-amber-700 font-bold tracking-wider">Já no funil CeL</div>
          <div className="text-3xl font-bold text-amber-800 mt-1">{kpi.ativos}</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Buscar município..." value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-9" data-testid="carteira-busca" />
        </div>
        <Select value={ufFilter || "__all__"} onValueChange={(v) => setUfFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="carteira-uf-filter"><SelectValue placeholder="Todas UFs" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{vendedoraScope ? "Todas minhas UFs" : "Todas UFs"}</SelectItem>
            {(vendedoraScope ? ufsAtuacao : ufsDisp).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          variant={somenteLivres ? "default" : "outline"}
          onClick={() => setSomenteLivres((v) => !v)}
          className={somenteLivres ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
          data-testid="carteira-toggle-livres"
        >
          <Filter className="w-4 h-4 mr-1" /> {somenteLivres ? "Só livres ✓" : "Só livres"}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400">Carregando carteira...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-300 rounded-2xl p-12 text-center">
          <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <div className="text-lg font-semibold text-slate-700">Nenhum município encontrado</div>
          <p className="text-sm text-slate-500 mt-1">
            {vendedoraScope && ufsAtuacao.length === 0
              ? "Peça ao gestor para definir suas UFs de atuação em Admin CeL."
              : "Ajuste os filtros ou aguarde municípios serem cadastrados no CRM."}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[11px] uppercase text-slate-500 tracking-wider">
                <th className="py-3 px-3">UF</th>
                <th className="py-3 px-3">Município</th>
                <th className="py-3 px-3">CNPJ</th>
                <th className="py-3 px-3">Status CeL</th>
                <th className="py-3 px-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((m) => (
                <tr key={m.id} className="hover:bg-slate-50" data-testid={`carteira-row-${m.id}`}>
                  <td className="py-2 px-3 font-mono text-xs text-slate-600">{m.uf}</td>
                  <td className="py-2 px-3">
                    <div className="font-semibold text-slate-900">{m.nome}</div>
                    {m.populacao_estimada && <div className="text-[11px] text-slate-500">Pop.: {Number(m.populacao_estimada).toLocaleString("pt-BR")}</div>}
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-600 font-mono">{m.cnpj || "—"}</td>
                  <td className="py-2 px-3">
                    {m.no_funil_cel ? (
                      <div className="flex flex-col gap-0.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border w-fit ${ESTAGIO_META[m.cel_estagio]?.color || "bg-slate-200 text-slate-700"}`}>
                          <CheckCircle2 className="w-3 h-3" /> {ESTAGIO_META[m.cel_estagio]?.label || m.cel_estagio}
                        </span>
                        <span className="text-[10px] text-slate-500 truncate max-w-[200px]" title={m.cel_indicador_nome}>por {m.cel_indicador_nome}</span>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-bold">
                        <XCircle className="w-3 h-3" /> Livre
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {m.no_funil_cel ? (
                      <span className="text-[11px] text-slate-400 italic">Já no funil</span>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => abrirProspectar(m)}
                        className="bg-sky-600 hover:bg-sky-700 text-white"
                        data-testid={`btn-prospectar-${m.id}`}
                      >
                        <Sparkles className="w-3.5 h-3.5 mr-1" /> Prospectar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Prospectar */}
      <Dialog open={!!prospectMun} onOpenChange={(v) => !v && setProspectMun(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="prospectar-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-sky-600" /> Prospectar {prospectMun?.nome}/{prospectMun?.uf}
            </DialogTitle>
            <DialogDescription>
              Prospecção fria da sua carteira. Registre o primeiro contato agora — o card entra direto em <b>Qualificação</b> atribuído a você, marcado como <i>prospecção direta</i> (não é indicação de terceiro).
            </DialogDescription>
          </DialogHeader>

          {/* Pessoas já cadastradas no município (Iter 52) */}
          <div className="border rounded-xl bg-slate-50 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-600" />
                <div className="text-xs uppercase font-bold text-slate-600 tracking-wider">Pessoas vinculadas ao município</div>
                <span className="text-xs text-slate-500">({pessoasLoading ? "..." : pessoasMun.length})</span>
              </div>
              {selectedPessoa && (
                <button className="text-[11px] text-red-600 font-semibold hover:underline" onClick={limparPessoa} data-testid="limpar-pessoa">Limpar</button>
              )}
            </div>
            {pessoasLoading ? (
              <div className="text-xs text-slate-400 py-2">Carregando pessoas...</div>
            ) : pessoasMun.length === 0 ? (
              <div className="text-xs text-slate-500 py-2 italic">Nenhuma pessoa cadastrada. Preencha os dados de contato abaixo.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-y-auto">
                {pessoasMun.map((p) => {
                  const isSel = selectedPessoa?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => escolherPessoa(p)}
                      className={`text-left p-2 rounded-lg border transition ${isSel ? "border-sky-500 bg-sky-50 ring-2 ring-sky-200" : "border-slate-200 bg-white hover:border-sky-300"}`}
                      data-testid={`pessoa-opt-${p.id}`}
                    >
                      <div className="font-semibold text-slate-800 text-sm truncate">{p.nome}</div>
                      <div className="text-[11px] text-slate-500 truncate">{p.funcao || "—"}{p.partido ? ` · ${p.partido}` : ""}</div>
                      <div className="text-[10px] text-slate-400 truncate mt-0.5">{p.whatsapp || p.telefone || "sem contato"}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Contato principal *</Label>
              <Input value={prospForm.contato_nome} onChange={(e) => setProspForm({ ...prospForm, contato_nome: e.target.value })} placeholder="Ex.: Presidente João Silva" data-testid="prosp-contato" />
              {selectedPessoa && <div className="text-[11px] text-sky-700 mt-0.5">✓ Vinculado a <b>{selectedPessoa.nome}</b> do CRM</div>}
            </div>
            <div>
              <Label>Cargo</Label>
              <Input value={prospForm.contato_cargo} onChange={(e) => setProspForm({ ...prospForm, contato_cargo: e.target.value })} placeholder="Presidente" />
            </div>
            <div>
              <Label>WhatsApp *</Label>
              <Input value={prospForm.contato_whatsapp} onChange={(e) => setProspForm({ ...prospForm, contato_whatsapp: e.target.value })} placeholder="(00) 99999-0000" data-testid="prosp-wa" />
            </div>
            <div className="md:col-span-2 border-t border-slate-200 pt-3 mt-1">
              <div className="text-[11px] uppercase font-bold text-slate-600 tracking-wider mb-1">
                Registrar primeiro contato *
              </div>
              <div className="text-[11px] text-slate-500 mb-2">
                Prospecção fria é diferente de indicação: registre agora o que você conversou. Ao salvar, o card já entra em <b>Qualificação</b>.
              </div>
            </div>
            <div>
              <Label>Canal *</Label>
              <Select value={prospForm.canal} onValueChange={(v) => setProspForm({ ...prospForm, canal: v })}>
                <SelectTrigger data-testid="prosp-canal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ligacao">Ligação</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">E-mail</SelectItem>
                  <SelectItem value="reuniao">Reunião</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Retornar em (opcional)</Label>
              <Input type="date" value={prospForm.data_retorno} onChange={(e) => setProspForm({ ...prospForm, data_retorno: e.target.value })} data-testid="prosp-retorno" />
            </div>
            <div className="md:col-span-2">
              <Label>O que foi conversado *</Label>
              <Textarea rows={3} value={prospForm.descricao} onChange={(e) => setProspForm({ ...prospForm, descricao: e.target.value })} placeholder="Ex.: liguei para o Presidente João, apresentei a CeL, ele demonstrou interesse em uma reunião..." data-testid="prosp-descricao" />
            </div>
            <div className="md:col-span-2">
              <Label>Observações internas</Label>
              <Textarea rows={2} value={prospForm.observacoes} onChange={(e) => setProspForm({ ...prospForm, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProspectMun(null)} disabled={busy}>Cancelar</Button>
            <Button onClick={enviarProspeccao} disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="prosp-submit">
              <Sparkles className="w-4 h-4 mr-1" /> {busy ? "Enviando..." : "Prospectar agora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
